import React, { useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { AssistantMarkdown } from './assistantMarkdown';
import { ActivityCopy, AgentErrorCode, MessageRole, MessageStatus, TOOL_ACTIVITY_COPY } from './aiAssistant.constants';
import {
    Activity,
    AssistantBody,
    AssistantRow,
    BrandMark,
    Caret,
    ErrorBox,
    Markdown,
    MessageActions,
    Notice,
    TypingDots,
    UserBubble,
} from './aiAssistant.styles';

const CopyAction = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const copy = () =>
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });

    return (
        <Tooltip title={copied ? 'Copied' : 'Copy'} placement="bottom">
            <IconButton size="small" onClick={copy} aria-label="Copy reply">
                {copied ? <CheckRoundedIcon fontSize="inherit" /> : <ContentCopyRoundedIcon fontSize="inherit" />}
            </IconButton>
        </Tooltip>
    );
};

const AssistantMessageView = ({ message, onRetry, onNewChat }) => {
    if (message.role === MessageRole.USER) {
        return <UserBubble>{message.text}</UserBubble>;
    }
    if (message.role === MessageRole.NOTICE) {
        return <Notice role="note">{message.text}</Notice>;
    }

    const streaming = message.status === MessageStatus.STREAMING;
    const sessionFull = message.errorCode === AgentErrorCode.SESSION_FULL;

    return (
        <AssistantRow>
            <BrandMark size={28} aria-hidden>
                <AutoAwesomeRoundedIcon />
            </BrandMark>

            <AssistantBody>
                {message.text ? (
                    <Markdown>
                        <AssistantMarkdown text={message.text} />
                        {streaming && <Caret aria-hidden />}
                    </Markdown>
                ) : (
                    streaming && (
                        <TypingDots aria-label="Assistant is typing">
                            <span />
                            <span />
                            <span />
                        </TypingDots>
                    )
                )}

                {message.activity && (
                    <Activity>
                        <SearchRoundedIcon sx={{ fontSize: 14 }} /> {ActivityCopy[message.activity] || TOOL_ACTIVITY_COPY}
                    </Activity>
                )}

                {message.status === MessageStatus.ERROR && (
                    <ErrorBox role="alert">
                        <span>{message.error}</span>
                        <Button size="small" color="inherit" onClick={sessionFull ? onNewChat : () => onRetry(message.id)}>
                            {sessionFull ? 'New chat' : 'Try again'}
                        </Button>
                    </ErrorBox>
                )}

                {message.status === MessageStatus.DONE && message.text && (
                    <MessageActions data-actions>
                        <CopyAction text={message.text} />
                    </MessageActions>
                )}
            </AssistantBody>
        </AssistantRow>
    );
};

/** Memoised: while a reply streams, only that reply's row re-renders — the others keep their props. */
export const AssistantMessage = React.memo(AssistantMessageView);
